import {
  Card,
  Text,
  SkeletonBodyText,
  IndexTable,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import type { Attendee } from "@features/events/types";
import { useNavigate, useParams } from "react-router-dom";
import { EVENT_CURRENCY_DEFAULT } from "@features/events/constants";
import { Currency, RenderDate } from "@shared/ui";
import { useCountries } from "@shared/hooks/common/useCountries";

export default function LatestAttendees({
  attendees,
  loading,
}: {
  attendees: Attendee[];
  loading: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();

  const { countries } = useCountries();

  const getCountryName = (countryNameOrCode: string) => {
    const found = countries.find((c) => c.iso3 === countryNameOrCode);
    return found ? found.name : countryNameOrCode;
  };

  const resourceName = {
    singular: t("eventDashboard.attendee"),
    plural: t("eventDashboard.attendees"),
  };
  const headings = [
    { title: t("eventDashboard.attendee") },
    { title: t("eventDashboard.phone") },
    { title: t("eventDashboard.email") },
    { title: t("eventDashboard.registered") },
    { title: t("eventDashboard.location") },
    { title: t("eventDashboard.events"), alignment: "end" as const },
    { title: t("eventDashboard.ticket") },
    { title: t("eventDashboard.totalPaid"), alignment: "end" as const },
  ] as [
    { title: string; alignment?: "start" | "end" },
    ...{ title: string; alignment?: "start" | "end" }[]
  ];
  const skeletonRows = [1, 2, 3, 4, 5];
  const skeletonCells = [1, 2, 3, 4, 5, 6, 7, 8];

  if (loading) {
    return (
      <Card padding="0">
        <IndexTable
          resourceName={resourceName}
          itemCount={skeletonRows.length}
          selectable={false}
          headings={headings}
        >
          {skeletonRows.map((rowId, index) => (
            <IndexTable.Row
              id={`latest-attendees-skeleton-row-${rowId}`}
              key={`latest-attendees-skeleton-row-${rowId}`}
              position={index}
            >
              {skeletonCells.map((cellId) => (
                <IndexTable.Cell
                  key={`latest-attendees-skeleton-cell-${rowId}-${cellId}`}
                >
                  <SkeletonBodyText lines={1} />
                </IndexTable.Cell>
              ))}
            </IndexTable.Row>
          ))}
        </IndexTable>
      </Card>
    );
  }

  const rowMarkup = attendees.map((a, index) => (
    <IndexTable.Row
      id={a.id}
      key={a.id}
      position={index}
      onClick={() => {
        navigate(`/e/${hcc}/attendees/${a.id}`);
      }}
    >
      {/* Name */}
      <IndexTable.Cell>
        <Text as="span" variant="bodyMd" fontWeight="bold">
          {a.firstName} {a.lastName}
        </Text>
      </IndexTable.Cell>
      {/* Phone */}
      <IndexTable.Cell>
        <Text as="span" variant="bodySm">
          {a.phoneCountryCode} {a.phoneNumber ?? ""}
        </Text>
      </IndexTable.Cell>
      {/* Email */}
      <IndexTable.Cell>
        <Text as="span" variant="bodySm">
          {a.email}
        </Text>
      </IndexTable.Cell>
      {/* Registered */}
      <IndexTable.Cell>
        {a.createdAt ? (
          <RenderDate date={a.createdAt} isAgoFormat={true} variant="bodySm" />
        ) : (
          "-"
        )}
      </IndexTable.Cell>
      {/* Location */}
      <IndexTable.Cell>
        <Text as="span" variant="bodySm">
          {[a.city, getCountryName(a.country)].filter(Boolean).join(", ")}
        </Text>
      </IndexTable.Cell>
      {/* Event count */}
      <IndexTable.Cell>
        <Text as="span" variant="bodySm" alignment="end">
          {a.totalVisitedEvents}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodySm">
          {a.ticket}
        </Text>
      </IndexTable.Cell>
      {/* Total spent */}
      <IndexTable.Cell>
        <Text as="span" variant="bodySm" alignment="end">
          <Currency
            value={a.paymentsTotalSpent ?? 0}
            currency={EVENT_CURRENCY_DEFAULT}
          />
        </Text>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Card padding="0">
      <IndexTable
        resourceName={resourceName}
        itemCount={attendees.length}
        selectable={false}
        headings={headings}
      >
        {rowMarkup}
      </IndexTable>
    </Card>
  );
}
